import * as THREE from 'three';
import {
  getBounding,
  getCorners,
  getSnapPoints,
  Point2D,
  pointInPolygon,
  polygonPolygonIntersect,
} from '../components/views/FebView/utils/geometryUtils';

// Spatial partitioning grid cell
interface GridCell {
  objects: THREE.Mesh[];
  bounds: {
    minX: number;
    maxX: number;
    minZ: number;
    maxZ: number;
  };
}

// Spatial partitioning grid
interface SpatialGrid {
  cells: GridCell[];
  cellSize: number;
  gridWidth: number;
  gridHeight: number;
  worldBounds: {
    minX: number;
    maxX: number;
    minZ: number;
    maxZ: number;
  };
}

// Collision group interface
export interface CollisionRule {
  group: string;
  collidesWithSelf: boolean;
  collidesWith: string[];
}

export class SnappingService {
  private _gridSize = 0.25;
  private _snapThreshold = 0.2;
  private _isSnappingEnabled = true;
  private _isGridSnappingEnabled = true;
  private _isEdgeSnappingEnabled = true;

  // Collision group settings
  private _collisionRules: CollisionRule[] = [];
  private _useCollisionGroups = false;

  // Debug visualization objects
  private _snapVisualizations: THREE.Object3D[] = [];

  // Add private rotation properties
  private _isRotationSnappingEnabled = true;
  private _rotationSnapAngle = Math.PI / 4; // 45 degrees by default

  // Alignment line visualization
  private _alignmentLines: THREE.Object3D[] = [];
  private _alignmentThreshold = 0.05; // How close objects need to be to be considered aligned

  // Spatial partitioning grid
  private _spatialGrid: SpatialGrid | null = null;
  private _spatialGridEnabled = true;
  private _spatialGridCellSize = 5.0; // Default size for spatial grid cells
  private _spatialGridNeedsUpdate = true;

  // Performance optimization flags
  private _useLoD = true; // Level of Detail optimization
  private _useFrustumCulling = true; // Only check objects in view
  private _maxObjectsToCheck = 50; // Maximum objects to check for snapping

  /**
   * Set the grid size for snapping
   * @param size Grid size in units
   */
  setGridSize(size: number): void {
    this._gridSize = size;
  }

  /**
   * Set the threshold distance for edge snapping
   * @param threshold Distance in units
   */
  setSnapThreshold(threshold: number): void {
    this._snapThreshold = threshold;
  }

  /**
   * Enable or disable all snapping
   * @param enabled Whether snapping is enabled
   */
  setSnappingEnabled(enabled: boolean): void {
    this._isSnappingEnabled = enabled;
  }

  /**
   * Enable or disable grid snapping
   * @param enabled Whether grid snapping is enabled
   */
  setGridSnappingEnabled(enabled: boolean): void {
    this._isGridSnappingEnabled = enabled;
  }

  /**
   * Enable or disable edge snapping
   * @param enabled Whether edge snapping is enabled
   */
  setEdgeSnappingEnabled(enabled: boolean): void {
    this._isEdgeSnappingEnabled = enabled;
  }

  /**
   * Set collision group rules
   * @param rules Array of collision rules
   */
  setCollisionRules(rules: CollisionRule[]): void {
    this._collisionRules = rules;
  }

  /**
   * Enable or disable collision group filtering
   * @param enabled Whether collision groups are used
   */
  setUseCollisionGroups(enabled: boolean): void {
    this._useCollisionGroups = enabled;
  }

  /**
   * Add a collision rule
   * @param rule Collision rule to add
   */
  addCollisionRule(rule: CollisionRule): void {
    // Remove any existing rule for this group
    this._collisionRules = this._collisionRules.filter(
      (r) => r.group !== rule.group
    );

    // Add the new rule
    this._collisionRules.push(rule);
  }

  // Performance optimization methods

  /**
   * Enable or disable spatial partitioning for performance optimization
   * @param enabled Whether spatial grid is enabled
   */
  setSpatialGridEnabled(enabled: boolean): void {
    this._spatialGridEnabled = enabled;
    this._spatialGridNeedsUpdate = true;
  }

  /**
   * Set the cell size for spatial partitioning
   * @param size Cell size in units
   */
  setSpatialGridCellSize(size: number): void {
    if (size <= 0) return;
    this._spatialGridCellSize = size;
    this._spatialGridNeedsUpdate = true;
  }

  /**
   * Enable or disable level of detail optimization
   * @param enabled Whether LoD is enabled
   */
  setLoDEnabled(enabled: boolean): void {
    this._useLoD = enabled;
  }

  /**
   * Enable or disable frustum culling
   * @param enabled Whether frustum culling is enabled
   */
  setFrustumCullingEnabled(enabled: boolean): void {
    this._useFrustumCulling = enabled;
  }

  /**
   * Set maximum objects to check for snapping
   * @param count Maximum number of objects
   */
  setMaxObjectsToCheck(count: number): void {
    if (count <= 0) return;
    this._maxObjectsToCheck = count;
  }

  /**
   * Build or update the spatial partitioning grid
   * @param scene The scene containing objects
   * @param floorPlane Optional floor plane to define world bounds
   */
  updateSpatialGrid(scene: THREE.Scene, floorPlane?: THREE.Mesh): void {
    if (!this._spatialGridEnabled) {
      this._spatialGrid = null;
      return;
    }

    // Determine world bounds
    let worldBounds: { minX: number; maxX: number; minZ: number; maxZ: number };

    if (floorPlane) {
      // Use floor plane to determine bounds
      const boundingBox = new THREE.Box3().setFromObject(floorPlane);
      worldBounds = {
        minX: boundingBox.min.x,
        maxX: boundingBox.max.x,
        minZ: boundingBox.min.z,
        maxZ: boundingBox.max.z,
      };
    } else {
      // Determine bounds from all objects
      worldBounds = {
        minX: Number.POSITIVE_INFINITY,
        maxX: Number.NEGATIVE_INFINITY,
        minZ: Number.POSITIVE_INFINITY,
        maxZ: Number.NEGATIVE_INFINITY,
      };

      scene.children.forEach((child) => {
        if (child instanceof THREE.Mesh && child.userData.draggable) {
          const boundingBox = new THREE.Box3().setFromObject(child);
          worldBounds.minX = Math.min(worldBounds.minX, boundingBox.min.x);
          worldBounds.maxX = Math.max(worldBounds.maxX, boundingBox.max.x);
          worldBounds.minZ = Math.min(worldBounds.minZ, boundingBox.min.z);
          worldBounds.maxZ = Math.max(worldBounds.maxZ, boundingBox.max.z);
        }
      });

      // Add padding
      const padding = 5;
      worldBounds.minX -= padding;
      worldBounds.maxX += padding;
      worldBounds.minZ -= padding;
      worldBounds.maxZ += padding;
    }

    // Calculate grid dimensions
    const gridWidth = Math.ceil(
      (worldBounds.maxX - worldBounds.minX) / this._spatialGridCellSize
    );
    const gridHeight = Math.ceil(
      (worldBounds.maxZ - worldBounds.minZ) / this._spatialGridCellSize
    );

    // Create cells
    const cells: GridCell[] = [];
    for (let x = 0; x < gridWidth; x++) {
      for (let z = 0; z < gridHeight; z++) {
        cells.push({
          objects: [],
          bounds: {
            minX: worldBounds.minX + x * this._spatialGridCellSize,
            maxX: worldBounds.minX + (x + 1) * this._spatialGridCellSize,
            minZ: worldBounds.minZ + z * this._spatialGridCellSize,
            maxZ: worldBounds.minZ + (z + 1) * this._spatialGridCellSize,
          },
        });
      }
    }

    // Create the grid
    this._spatialGrid = {
      cells,
      cellSize: this._spatialGridCellSize,
      gridWidth,
      gridHeight,
      worldBounds,
    };

    // Add objects to cells
    this.populateSpatialGrid(scene);

    this._spatialGridNeedsUpdate = false;
  }

  /**
   * Populate the spatial grid with objects from the scene
   * @param scene The scene containing objects
   */
  private populateSpatialGrid(scene: THREE.Scene): void {
    if (!this._spatialGrid) return;

    // Clear all cells
    this._spatialGrid.cells.forEach((cell) => {
      cell.objects = [];
    });

    // Add objects to appropriate cells
    scene.children.forEach((child) => {
      if (child instanceof THREE.Mesh && child.userData.draggable) {
        const boundingBox = new THREE.Box3().setFromObject(child);

        // Find cells that intersect with this object
        const minCellX = this.worldToGridX(boundingBox.min.x);
        const maxCellX = this.worldToGridX(boundingBox.max.x);
        const minCellZ = this.worldToGridZ(boundingBox.min.z);
        const maxCellZ = this.worldToGridZ(boundingBox.max.z);

        // Add object to all intersecting cells
        for (let x = minCellX; x <= maxCellX; x++) {
          for (let z = minCellZ; z <= maxCellZ; z++) {
            const cell = this.getCell(x, z);
            if (cell) {
              cell.objects.push(child);
            }
          }
        }
      }
    });
  }

  /**
   * Convert world X coordinate to grid X index
   */
  private worldToGridX(x: number): number {
    if (!this._spatialGrid) return 0;
    return Math.floor(
      (x - this._spatialGrid.worldBounds.minX) / this._spatialGridCellSize
    );
  }

  /**
   * Convert world Z coordinate to grid Z index
   */
  private worldToGridZ(z: number): number {
    if (!this._spatialGrid) return 0;
    return Math.floor(
      (z - this._spatialGrid.worldBounds.minZ) / this._spatialGridCellSize
    );
  }

  /**
   * Get a cell at the specified grid coordinates
   */
  private getCell(x: number, z: number): GridCell | null {
    if (!this._spatialGrid) return null;

    if (
      x < 0 ||
      x >= this._spatialGrid.gridWidth ||
      z < 0 ||
      z >= this._spatialGrid.gridHeight
    ) {
      return null;
    }

    const index = x * this._spatialGrid.gridHeight + z;
    return this._spatialGrid.cells[index];
  }

  /**
   * Get all objects that could potentially interact with the given object
   * using spatial partitioning for efficiency
   */
  getPotentialInteractionObjects(
    object: THREE.Mesh,
    scene: THREE.Scene,
    camera?: THREE.Camera,
    options: {
      maxDistance?: number;
      onlyDraggable?: boolean;
      excludeObject?: THREE.Mesh | null;
    } = {}
  ): THREE.Mesh[] {
    // Update spatial grid if needed
    if (this._spatialGridEnabled && this._spatialGridNeedsUpdate) {
      this.updateSpatialGrid(scene);
    }

    if (!this._spatialGridEnabled || !this._spatialGrid) {
      // Fallback to checking all objects
      return scene.children.filter(
        (child) =>
          child instanceof THREE.Mesh &&
          child !== object &&
          child !== options.excludeObject &&
          (!options.onlyDraggable || child.userData.draggable)
      ) as THREE.Mesh[];
    }

    // Get object bounds
    const boundingBox = new THREE.Box3().setFromObject(object);

    // Find cells that intersect with this object
    const minCellX = this.worldToGridX(
      boundingBox.min.x - (options.maxDistance || 0)
    );
    const maxCellX = this.worldToGridX(
      boundingBox.max.x + (options.maxDistance || 0)
    );
    const minCellZ = this.worldToGridZ(
      boundingBox.min.z - (options.maxDistance || 0)
    );
    const maxCellZ = this.worldToGridZ(
      boundingBox.max.z + (options.maxDistance || 0)
    );

    // Collect objects from all intersecting cells
    const potentialObjects = new Set<THREE.Mesh>();

    for (let x = minCellX; x <= maxCellX; x++) {
      for (let z = minCellZ; z <= maxCellZ; z++) {
        const cell = this.getCell(x, z);
        if (cell) {
          cell.objects.forEach((cellObject) => {
            if (
              cellObject !== object &&
              cellObject !== options.excludeObject &&
              (!options.onlyDraggable || cellObject.userData.draggable)
            ) {
              potentialObjects.add(cellObject);
            }
          });
        }
      }
    }

    // Apply frustum culling if enabled and camera is provided
    let filteredObjects = Array.from(potentialObjects);

    if (this._useFrustumCulling && camera) {
      const frustum = new THREE.Frustum();
      const projScreenMatrix = new THREE.Matrix4();

      projScreenMatrix.multiplyMatrices(
        camera.projectionMatrix,
        camera.matrixWorldInverse
      );

      frustum.setFromProjectionMatrix(projScreenMatrix);

      filteredObjects = filteredObjects.filter((obj) => {
        const objBoundingBox = new THREE.Box3().setFromObject(obj);
        const objBoundingSphere = new THREE.Sphere();
        objBoundingBox.getBoundingSphere(objBoundingSphere);
        return frustum.intersectsSphere(objBoundingSphere);
      });
    }

    // Apply distance-based filtering for Level of Detail if enabled
    if (
      this._useLoD &&
      camera &&
      filteredObjects.length > this._maxObjectsToCheck
    ) {
      const cameraPosition = new THREE.Vector3();
      camera.getWorldPosition(cameraPosition);

      // Sort by distance to camera
      filteredObjects.sort((a, b) => {
        const posA = new THREE.Vector3();
        const posB = new THREE.Vector3();

        a.getWorldPosition(posA);
        b.getWorldPosition(posB);

        return (
          posA.distanceTo(cameraPosition) - posB.distanceTo(cameraPosition)
        );
      });

      // Limit the number of objects
      filteredObjects = filteredObjects.slice(0, this._maxObjectsToCheck);
    }

    return filteredObjects;
  }

  /**
   * Check if two objects should collide based on their collision groups
   * @param object1 First object
   * @param object2 Second object
   * @returns true if objects should collide
   */
  shouldObjectsCollide(object1: THREE.Mesh, object2: THREE.Mesh): boolean {
    if (!this._useCollisionGroups) {
      return true; // If collision groups are disabled, all objects collide
    }

    const group1 = object1.userData.collisionGroup as string;
    const group2 = object2.userData.collisionGroup as string;

    if (!group1 || !group2) {
      return true; // If either object has no group, default to collision
    }

    // Find collision rules for the first object's group
    const rule = this._collisionRules.find((r) => r.group === group1);
    if (!rule) {
      return true; // No specific rule, default to collision
    }

    // Check if the second object's group is in the collidesWith list
    if (rule.collidesWith.includes(group2)) {
      return true;
    }

    // Check if objects are in the same group and collidesWithSelf is true
    if (group1 === group2 && rule.collidesWithSelf) {
      return true;
    }

    return false;
  }

  /**
   * Snap a position to the nearest grid point if grid snapping is enabled
   * @param position Position to snap
   * @param options Optional settings for grid snapping
   * @returns The snapped position
   */
  snapToGrid(
    position: THREE.Vector3,
    options: {
      gridSize?: number; // Override the default grid size
      snapPoints?: string[]; // Which points to snap ('center', 'corners', 'edges', 'all')
      snapStrength?: number; // How strong the snapping is (0-1)
    } = {}
  ): THREE.Vector3 {
    if (!this._isSnappingEnabled || !this._isGridSnappingEnabled) {
      return position.clone();
    }

    // Use provided grid size or default
    const gridSize = options.gridSize ?? this._gridSize;
    const snapStrength = options.snapStrength ?? 1.0;

    if (snapStrength <= 0) {
      return position.clone();
    }

    const snappedPosition = position.clone();

    // Calculate snapped position
    const snappedX = Math.round(snappedPosition.x / gridSize) * gridSize;
    const snappedZ = Math.round(snappedPosition.z / gridSize) * gridSize;

    // Apply snap strength (linear interpolation between original and snapped)
    snappedPosition.x = position.x + (snappedX - position.x) * snapStrength;
    snappedPosition.z = position.z + (snappedZ - position.z) * snapStrength;

    return snappedPosition;
  }

  /**
   * Snap an object to the nearest edge of other objects if edge snapping is enabled
   * @param object Object to snap
   * @param scene Scene containing other objects
   * @param options Optional settings for edge snapping
   * @returns Information about the snapping that occurred
   */
  snapToEdge(
    object: THREE.Mesh,
    scene: THREE.Scene,
    options: {
      threshold?: number; // Override the default snap threshold
      snapPoints?: string[]; // Which points to snap ('center', 'corners', 'edges', 'all')
      visualize?: boolean; // Whether to show visual indicators
      snapStrength?: number; // How strong the snapping is (0-1)
      priorityGroups?: string[]; // Object groups to prioritize for snapping
      camera?: THREE.Camera; // Camera for frustum culling and LoD
    } = {}
  ): {
    snapped: boolean;
    snapX: number;
    snapZ: number;
    snapPoint?: Point2D;
    targetPoint?: Point2D;
    targetObject?: THREE.Mesh;
  } {
    if (!this._isSnappingEnabled || !this._isEdgeSnappingEnabled) {
      return { snapped: false, snapX: 0, snapZ: 0 };
    }

    // Use provided options or defaults
    const threshold = options.threshold ?? this._snapThreshold;
    const visualize = options.visualize ?? true;
    const snapStrength = options.snapStrength ?? 1.0;

    if (snapStrength <= 0) {
      return { snapped: false, snapX: 0, snapZ: 0 };
    }

    // Clear previous snap visualizations
    if (visualize) {
      this.clearSnapVisualizations(scene);
    }

    // Use spatial partitioning to efficiently find relevant objects
    const draggableObjects = this.getPotentialInteractionObjects(
      object,
      scene,
      options.camera,
      {
        maxDistance: threshold * 2, // Look a bit further than the threshold
        onlyDraggable: true,
      }
    ).filter(
      (child) =>
        !this.isLinkedToObject(object, child) &&
        (!this._useCollisionGroups || this.shouldObjectsCollide(object, child))
    );

    if (draggableObjects.length === 0) {
      return { snapped: false, snapX: 0, snapZ: 0 };
    }

    // Sort objects by priority if priority groups are specified
    if (options.priorityGroups && options.priorityGroups.length > 0) {
      draggableObjects.sort((a, b) => {
        const groupA = a.userData.collisionGroup as string;
        const groupB = b.userData.collisionGroup as string;

        const priorityA = options.priorityGroups?.indexOf(groupA) ?? -1;
        const priorityB = options.priorityGroups?.indexOf(groupB) ?? -1;

        // Higher priority (lower index) comes first
        if (priorityA !== -1 && priorityB !== -1) {
          return priorityA - priorityB;
        }
        // Any priority group comes before non-priority
        if (priorityA !== -1) return -1;
        if (priorityB !== -1) return 1;

        return 0;
      });
    }

    let snapX = 0,
      snapZ = 0;
    let minSnapDiffX = threshold;
    let minSnapDiffZ = threshold;
    let snappedToEdge = false;
    let snapPoint: Point2D | undefined;
    let targetPoint: Point2D | undefined;
    let targetObject: THREE.Mesh | undefined;

    // Get object bounds for snapping calculations
    const bounds = getBounding(object);
    const minX = bounds.min.x;
    const maxX = bounds.max.x;
    const minZ = bounds.min.z;
    const maxZ = bounds.max.z;
    const width = maxX - minX;
    const depth = maxZ - minZ;
    const centerX = (minX + maxX) / 2;
    const centerZ = (minZ + maxZ) / 2;

    // Points on this object to consider for snapping
    let objectSnapPoints: Point2D[] = [];

    // Determine which snap points to use
    const snapPointsOption = options.snapPoints ?? ['all'];

    if (
      snapPointsOption.includes('all') ||
      snapPointsOption.includes('center')
    ) {
      objectSnapPoints.push({ x: centerX, z: centerZ }); // Center
    }

    if (
      snapPointsOption.includes('all') ||
      snapPointsOption.includes('corners')
    ) {
      objectSnapPoints = objectSnapPoints.concat([
        { x: minX, z: minZ }, // Bottom-left
        { x: maxX, z: minZ }, // Bottom-right
        { x: maxX, z: maxZ }, // Top-right
        { x: minX, z: maxZ }, // Top-left
      ]);
    }

    if (
      snapPointsOption.includes('all') ||
      snapPointsOption.includes('edges')
    ) {
      objectSnapPoints = objectSnapPoints.concat([
        { x: minX + width / 2, z: minZ }, // Bottom center
        { x: minX + width / 2, z: maxZ }, // Top center
        { x: minX, z: minZ + depth / 2 }, // Left center
        { x: maxX, z: minZ + depth / 2 }, // Right center
      ]);
    }

    // Special case: if no snap points are specified, use all
    if (objectSnapPoints.length === 0) {
      objectSnapPoints = [
        { x: centerX, z: centerZ }, // Center
        { x: minX, z: minZ }, // Bottom-left
        { x: maxX, z: minZ }, // Bottom-right
        { x: maxX, z: maxZ }, // Top-right
        { x: minX, z: maxZ }, // Top-left
        { x: minX + width / 2, z: minZ }, // Bottom center
        { x: minX + width / 2, z: maxZ }, // Top center
        { x: minX, z: minZ + depth / 2 }, // Left center
        { x: maxX, z: minZ + depth / 2 }, // Right center
      ];
    }

    for (const targetObj of draggableObjects) {
      const targetSnapPoints = getSnapPoints(targetObj);

      for (const tPoint of targetSnapPoints) {
        for (const oPoint of objectSnapPoints) {
          // Check snapping for X coordinate
          const diffX = Math.abs(oPoint.x - tPoint.x);
          if (diffX < minSnapDiffX) {
            minSnapDiffX = diffX;
            snapX = tPoint.x - oPoint.x;
            snappedToEdge = true;
            snapPoint = oPoint;
            targetPoint = tPoint;
            targetObject = targetObj;

            // Visualize X snapping
            if (visualize) {
              this.createSnapVisualization(
                scene,
                new THREE.Vector3(tPoint.x, 0.01, oPoint.z),
                new THREE.Vector3(tPoint.x, 0.01, tPoint.z),
                0x00ff00
              );
            }
          }

          // Check snapping for Z coordinate
          const diffZ = Math.abs(oPoint.z - tPoint.z);
          if (diffZ < minSnapDiffZ) {
            minSnapDiffZ = diffZ;
            snapZ = tPoint.z - oPoint.z;
            snappedToEdge = true;
            snapPoint = oPoint;
            targetPoint = tPoint;
            targetObject = targetObj;

            // Visualize Z snapping
            if (visualize) {
              this.createSnapVisualization(
                scene,
                new THREE.Vector3(oPoint.x, 0.01, tPoint.z),
                new THREE.Vector3(tPoint.x, 0.01, tPoint.z),
                0x0000ff
              );
            }
          }

          // Check for both X and Z snapping (corner to corner)
          if (diffX < threshold && diffZ < threshold) {
            // Create a diagonal line for corner snap
            if (visualize) {
              this.createSnapVisualization(
                scene,
                new THREE.Vector3(oPoint.x, 0.01, oPoint.z),
                new THREE.Vector3(tPoint.x, 0.01, tPoint.z),
                0xff00ff
              );
            }
          }
        }
      }
    }

    // Apply the snapping with snap strength
    if (minSnapDiffX < threshold) {
      snapX = snapX * snapStrength;
      object.position.x += snapX;
    }

    if (minSnapDiffZ < threshold) {
      snapZ = snapZ * snapStrength;
      object.position.z += snapZ;
    }

    return {
      snapped: snappedToEdge,
      snapX,
      snapZ,
      snapPoint,
      targetPoint,
      targetObject,
    };
  }

  /**
   * Check if an object overlaps with any other objects in the scene
   * @param object Object to check
   * @param scene Scene containing other objects
   * @param excludeObject Optional object to exclude from check
   * @param options Optional settings for collision detection
   * @returns Array of overlapping objects
   */
  checkOverlap(
    object: THREE.Mesh,
    scene: THREE.Scene,
    excludeObject: THREE.Mesh | null = null,
    options: {
      objectTypes?: string[]; // Filter by object types
      onlyCheckVisible?: boolean; // Only check visible objects
      skipBoundingBoxCheck?: boolean; // Skip initial bounding box check for greater precision
      camera?: THREE.Camera; // Camera for frustum culling and LoD
      useSpatialGrid?: boolean; // Override spatial grid setting
    } = {}
  ): THREE.Mesh[] {
    const overlappingObjects: THREE.Mesh[] = [];

    // Performance optimization: Get object's bounding box once, using custom box if available
    const objectBounds = object.userData.collisionBox
      ? (object.userData.collisionBox as THREE.Box3)
          .clone()
          .translate(object.position)
      : new THREE.Box3().setFromObject(object);

    const objectCorners = getCorners(object, object.userData.collisionBox);

    // Use spatial partitioning if enabled
    const useSpatialGrid =
      options.useSpatialGrid !== undefined
        ? options.useSpatialGrid
        : this._spatialGridEnabled;

    // Get potential objects using spatial partitioning
    const potentialObjects = useSpatialGrid
      ? this.getPotentialInteractionObjects(object, scene, options.camera, {
          excludeObject: excludeObject,
          onlyDraggable: true,
        })
      : (scene.children.filter(
          (child) =>
            child instanceof THREE.Mesh &&
            child !== object &&
            child !== excludeObject &&
            child.userData.draggable
        ) as THREE.Mesh[]);

    // Filter objects
    const draggableObjects = potentialObjects.filter(
      (child) =>
        !this.isLinkedToObject(object, child) &&
        !child.userData.isCollisionVisualization &&
        // Apply optional type filtering
        (!options.objectTypes ||
          (child.userData.type &&
            options.objectTypes.includes(child.userData.type))) &&
        // Apply optional visibility filtering
        (!options.onlyCheckVisible || child.visible) &&
        // Apply collision group filtering if enabled
        (!this._useCollisionGroups || this.shouldObjectsCollide(object, child))
    );

    // Early out if no objects to check
    if (draggableObjects.length === 0) {
      return [];
    }

    // Log performance metrics if in debug mode
    const startTime = performance.now();

    for (const otherObject of draggableObjects) {
      // Performance optimization: Quick bounding box check first unless disabled
      if (!options.skipBoundingBoxCheck) {
        // Use custom collision box if available
        const otherBounds = otherObject.userData.collisionBox
          ? (otherObject.userData.collisionBox as THREE.Box3)
              .clone()
              .translate(otherObject.position)
          : new THREE.Box3().setFromObject(otherObject);

        // If bounding boxes don't intersect, skip detailed check
        if (!objectBounds.intersectsBox(otherBounds)) {
          continue;
        }
      }

      // Detailed check with polygon intersection
      const otherCorners = getCorners(
        otherObject,
        otherObject.userData.collisionBox
      );
      if (polygonPolygonIntersect(objectCorners, otherCorners)) {
        overlappingObjects.push(otherObject);
      }
    }

    // Performance metrics
    const endTime = performance.now();
    const elapsedTime = endTime - startTime;

    // Log performance metrics if enabled and more than 10 objects were checked
    if (
      process.env.NODE_ENV === 'development' &&
      draggableObjects.length > 10
    ) {
      console.debug(
        `[SnappingService] Overlap check: ${elapsedTime.toFixed(2)}ms, ` +
          `Objects checked: ${draggableObjects.length}, ` +
          `Overlapping: ${overlappingObjects.length}, ` +
          `Spatial grid: ${useSpatialGrid ? 'enabled' : 'disabled'}`
      );
    }

    return overlappingObjects;
  }

  /**
   * Check if an object is outside the room boundaries
   * @param object Object to check
   * @param floorPlane Floor plane defining the room
   * @param options Optional settings for boundary checking
   * @returns true if object is out of bounds
   */
  checkOutOfBounds(
    object: THREE.Mesh,
    floorPlane: THREE.Mesh,
    options: {
      tolerance?: number; // How much overlap is allowed (0-1, where 1 means fully in bounds)
      customBoundary?: THREE.Vector3[]; // Optional custom boundary points (overrides floorPlane)
      useObjectCenter?: boolean; // Only check object center instead of all corners
    } = {}
  ): boolean {
    // Set default tolerance
    const tolerance = options.tolerance ?? 0;

    // If using custom boundary points
    if (options.customBoundary && options.customBoundary.length >= 3) {
      const customBoundaryPoints: Point2D[] = options.customBoundary.map(
        (p) => ({ x: p.x, z: p.z })
      );

      if (options.useObjectCenter) {
        // Only check if the center point is inside the boundary
        return !pointInPolygon(
          object.position.x,
          object.position.z,
          customBoundaryPoints
        );
      } else {
        // Check all corners
        const objectCorners = getCorners(object);
        let cornersOutside = 0;

        for (const corner of objectCorners) {
          if (!pointInPolygon(corner.x, corner.z, customBoundaryPoints)) {
            cornersOutside++;
          }
        }

        // Object is out of bounds if more than (1-tolerance) of corners are outside
        const maxCornersOutside = Math.floor(
          objectCorners.length * (1 - tolerance)
        );
        return cornersOutside > maxCornersOutside;
      }
    }

    // Using floor plane boundary
    const objectCorners = getCorners(object);

    // Get the floor bounds
    const floorBounds = new THREE.Box3().setFromObject(floorPlane);

    if (options.useObjectCenter) {
      // Only check center point
      return (
        object.position.x < floorBounds.min.x ||
        object.position.x > floorBounds.max.x ||
        object.position.z < floorBounds.min.z ||
        object.position.z > floorBounds.max.z
      );
    }

    // Count corners that are outside
    let cornersOutside = 0;
    for (const corner of objectCorners) {
      if (
        corner.x < floorBounds.min.x ||
        corner.x > floorBounds.max.x ||
        corner.z < floorBounds.min.z ||
        corner.z > floorBounds.max.z
      ) {
        cornersOutside++;
      }
    }

    // Object is out of bounds if more than (1-tolerance) of corners are outside
    const maxCornersOutside = Math.floor(
      objectCorners.length * (1 - tolerance)
    );
    return cornersOutside > maxCornersOutside;
  }

  /**
   * Check if two objects are linked (and should not trigger collision)
   * @param object1 First object
   * @param object2 Second object
   * @returns true if objects are linked
   */
  isLinkedToObject(object1: THREE.Mesh, object2: THREE.Mesh): boolean {
    if (
      !object1.userData.linkedItems ||
      !Array.isArray(object1.userData.linkedItems)
    ) {
      return false;
    }

    return object1.userData.linkedItems.includes(object2);
  }

  /**
   * Create a visualization line for snapping
   * @param scene Scene to add visualization to
   * @param start Start point of line
   * @param end End point of line
   * @param color Line color
   */
  private createSnapVisualization(
    scene: THREE.Scene,
    start: THREE.Vector3,
    end: THREE.Vector3,
    color = 0xffff00
  ): void {
    const material = new THREE.LineBasicMaterial({
      color: color,
      linewidth: 2,
      transparent: true,
      opacity: 0.7,
    });

    const points = [start, end];
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const line = new THREE.Line(geometry, material);
    line.userData.isSnapVisualization = true;

    scene.add(line);
    this._snapVisualizations.push(line);

    // Auto-clear visualization after 1 second
    setTimeout(() => {
      if (scene.children.includes(line)) {
        scene.remove(line);
        const index = this._snapVisualizations.indexOf(line);
        if (index !== -1) {
          this._snapVisualizations.splice(index, 1);
        }
      }
    }, 1000);
  }

  /**
   * Clear all snap visualizations from the scene
   * @param scene Scene containing visualizations
   */
  clearSnapVisualizations(scene: THREE.Scene): void {
    for (const visualization of this._snapVisualizations) {
      if (scene.children.includes(visualization)) {
        scene.remove(visualization);
      }
    }
    this._snapVisualizations = [];
  }

  /**
   * Clear all alignment line visualizations from the scene
   * @param scene Scene containing visualizations
   */
  clearAlignmentLines(scene: THREE.Scene): void {
    for (const line of this._alignmentLines) {
      if (scene.children.includes(line)) {
        scene.remove(line);
      }
    }
    this._alignmentLines = [];
  }

  /**
   * Update alignment line visualizations between objects
   * @param scene Scene containing objects
   * @param options Optional settings for alignment visualization
   */
  updateAlignmentLines(
    scene: THREE.Scene,
    options: {
      threshold?: number; // Override default alignment threshold
      showVertical?: boolean; // Show vertical alignment lines
      showHorizontal?: boolean; // Show horizontal alignment lines
    } = {}
  ): void {
    // Clear existing alignment lines
    this.clearAlignmentLines(scene);

    const threshold = options.threshold ?? this._alignmentThreshold;
    const showVertical = options.showVertical ?? true;
    const showHorizontal = options.showHorizontal ?? true;

    // Find all objects with red sprites (center point visualizations)
    const objectsWithSprites = scene.children.filter(
      (child) =>
        child instanceof THREE.Mesh &&
        child.userData.draggable &&
        child.children.some(
          (grandchild) => grandchild.userData.isCenterPointVisualization
        )
    ) as THREE.Mesh[];

    if (objectsWithSprites.length < 2) {
      return; // Need at least 2 objects to create alignment lines
    }

    // Group objects by alignment
    const verticalGroups: THREE.Mesh[][] = [];
    const horizontalGroups: THREE.Mesh[][] = [];

    // Check for vertical alignment (same X coordinate)
    if (showVertical) {
      for (let i = 0; i < objectsWithSprites.length; i++) {
        const object1 = objectsWithSprites[i];
        const group = [object1];

        for (let j = i + 1; j < objectsWithSprites.length; j++) {
          const object2 = objectsWithSprites[j];
          const xDiff = Math.abs(object1.position.x - object2.position.x);

          if (xDiff <= threshold) {
            group.push(object2);
          }
        }

        if (group.length > 1) {
          // Check if this group is already covered by a previous group
          const isNewGroup = !verticalGroups.some((existingGroup) =>
            group.every((obj) => existingGroup.includes(obj))
          );

          if (isNewGroup) {
            verticalGroups.push(group);
          }
        }
      }
    }

    // Check for horizontal alignment (same Z coordinate)
    if (showHorizontal) {
      for (let i = 0; i < objectsWithSprites.length; i++) {
        const object1 = objectsWithSprites[i];
        const group = [object1];

        for (let j = i + 1; j < objectsWithSprites.length; j++) {
          const object2 = objectsWithSprites[j];
          const zDiff = Math.abs(object1.position.z - object2.position.z);

          if (zDiff <= threshold) {
            group.push(object2);
          }
        }

        if (group.length > 1) {
          // Check if this group is already covered by a previous group
          const isNewGroup = !horizontalGroups.some((existingGroup) =>
            group.every((obj) => existingGroup.includes(obj))
          );

          if (isNewGroup) {
            horizontalGroups.push(group);
          }
        }
      }
    }

    // Create lines for vertical groups
    verticalGroups.forEach((group) => {
      this.createAlignmentLinesForGroup(scene, group, 'vertical');
    });

    // Create lines for horizontal groups
    horizontalGroups.forEach((group) => {
      this.createAlignmentLinesForGroup(scene, group, 'horizontal');
    });
  }

  /**
   * Create alignment lines for a group of aligned objects
   * @param scene Scene to add lines to
   * @param group Array of aligned objects
   * @param alignmentType Type of alignment ('vertical' or 'horizontal')
   */
  private createAlignmentLinesForGroup(
    scene: THREE.Scene,
    group: THREE.Mesh[],
    alignmentType: 'vertical' | 'horizontal'
  ): void {
    if (group.length < 2) return;

    // Get the red sprite positions for each object in the group
    const spritePositions: THREE.Vector3[] = [];

    group.forEach((object) => {
      const redSprite = object.children.find(
        (child) => child.userData.isCenterPointVisualization
      );

      if (redSprite) {
        // Get world position of the red sprite
        const worldPosition = new THREE.Vector3();
        redSprite.getWorldPosition(worldPosition);
        spritePositions.push(worldPosition);
      }
    });

    if (spritePositions.length < 2) return;

    // Sort positions based on alignment type
    if (alignmentType === 'vertical') {
      // Sort by Z coordinate (top to bottom)
      spritePositions.sort((a, b) => a.z - b.z);
    } else {
      // Sort by X coordinate (left to right)
      spritePositions.sort((a, b) => a.x - b.x);
    }

    // Create lines connecting consecutive sprites
    for (let i = 0; i < spritePositions.length - 1; i++) {
      const start = spritePositions[i];
      const end = spritePositions[i + 1];

      // Create lime green line
      const material = new THREE.LineBasicMaterial({
        color: 0x32cd32, // Lime green
        linewidth: 3,
        transparent: true,
        opacity: 0.8,
        depthTest: false,
        depthWrite: false,
      });

      const points = [start.clone(), end.clone()];
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(geometry, material);

      line.renderOrder = 998; // Render before red sprites but after most objects
      line.userData.isAlignmentLine = true;
      line.userData.alignmentType = alignmentType;

      scene.add(line);
      this._alignmentLines.push(line);
    }
  }

  /**
   * Set the threshold for alignment detection
   * @param threshold Distance threshold for considering objects aligned
   */
  setAlignmentThreshold(threshold: number): void {
    this._alignmentThreshold = threshold;
  }

  /**
   * Get the current alignment threshold
   * @returns Current alignment threshold
   */
  getAlignmentThreshold(): number {
    return this._alignmentThreshold;
  }

  /**
   * Debug function to visualize snap points for an object
   * @param scene Scene to add visualization to
   * @param object Object to visualize snap points for
   */
  visualizeSnapPoints(scene: THREE.Scene, object: THREE.Mesh): void {
    const snapPoints = getSnapPoints(object);

    // Remove existing visualizations
    scene.children.forEach((child) => {
      if (child.userData.isSnapPointVisualization) {
        scene.remove(child);
      }
    });

    // Create visual markers for each snap point
    snapPoints.forEach((point) => {
      const geometry = new THREE.SphereGeometry(0.03, 16, 16);
      const material = new THREE.MeshBasicMaterial({ color: 0xffff00 });
      const sphere = new THREE.Mesh(geometry, material);

      sphere.position.set(point.x, 0.02, point.z);
      sphere.userData.isSnapPointVisualization = true;

      scene.add(sphere);
    });
  }

  /**
   * Visualize room boundaries for debugging
   * @param scene The Three.js scene
   * @param floorPlane Floor plane defining the room, or null if using custom boundary
   * @param options Optional settings for visualization
   * @returns Array of visualization objects created
   */
  visualizeBoundary(
    scene: THREE.Scene,
    floorPlane: THREE.Mesh | null,
    options: {
      color?: number; // Color of boundary visualization
      height?: number; // Height of boundary walls
      customBoundary?: THREE.Vector3[]; // Optional custom boundary points
      showWarningZone?: boolean; // Show an inner boundary as a warning zone
      warningZoneOffset?: number; // Distance from boundary for warning zone
    } = {}
  ): THREE.Object3D[] {
    // Default options
    const color = options.color ?? 0xff0000;
    const height = options.height ?? 0.5;
    const warningZoneOffset = options.warningZoneOffset ?? 0.5;

    // Remove any existing boundary visualizations
    scene.children.forEach((child) => {
      if (child.userData.isBoundaryVisualization) {
        scene.remove(child);
      }
    });

    const visualizations: THREE.Object3D[] = [];

    // Function to create a boundary visualization from points
    const createBoundaryFromPoints = (
      points: THREE.Vector3[],
      isWarningZone = false
    ) => {
      // Create a line to visualize the boundary
      const material = new THREE.LineBasicMaterial({
        color: isWarningZone ? 0xffaa00 : color, // Use amber for warning zone
        linewidth: 2,
        transparent: true,
        opacity: isWarningZone ? 0.5 : 0.8,
      });

      // Create points for the boundary line
      const linePoints: THREE.Vector3[] = [];

      // Add all points at floor level and then again at height
      for (let i = 0; i <= points.length; i++) {
        const point = points[i % points.length]; // Wrap around to close the loop
        linePoints.push(new THREE.Vector3(point.x, 0.01, point.z)); // Slightly above floor
      }

      if (!isWarningZone) {
        // For the main boundary, add vertical lines at corners
        for (let i = 0; i < points.length; i++) {
          const point = points[i];
          linePoints.push(new THREE.Vector3(point.x, 0.01, point.z)); // Start at floor
          linePoints.push(new THREE.Vector3(point.x, height, point.z)); // Go up

          // Connect to next point at height
          const nextPoint = points[(i + 1) % points.length];
          linePoints.push(new THREE.Vector3(nextPoint.x, height, nextPoint.z));
        }
      }

      const geometry = new THREE.BufferGeometry().setFromPoints(linePoints);
      const line = new THREE.LineSegments(geometry, material);

      line.userData.isBoundaryVisualization = true;
      scene.add(line);
      visualizations.push(line);

      return line;
    };

    // Use custom boundary if provided, otherwise use floor plane
    if (options.customBoundary && options.customBoundary.length >= 3) {
      createBoundaryFromPoints(options.customBoundary);

      // Create warning zone if requested
      if (options.showWarningZone) {
        // Create a smaller boundary inside the main one
        const center = new THREE.Vector3();
        options.customBoundary.forEach((p) => center.add(p));
        center.divideScalar(options.customBoundary.length);

        const warningPoints = options.customBoundary.map((p) => {
          const dir = new THREE.Vector3().subVectors(p, center).normalize();
          return new THREE.Vector3().addVectors(
            p,
            dir.multiplyScalar(-warningZoneOffset)
          );
        });

        createBoundaryFromPoints(warningPoints, true);
      }
    } else if (floorPlane) {
      // Use floor plane to create boundary
      const floorBounds = new THREE.Box3().setFromObject(floorPlane);

      // Create points for rectangle
      const boundaryPoints = [
        new THREE.Vector3(floorBounds.min.x, 0, floorBounds.min.z),
        new THREE.Vector3(floorBounds.max.x, 0, floorBounds.min.z),
        new THREE.Vector3(floorBounds.max.x, 0, floorBounds.max.z),
        new THREE.Vector3(floorBounds.min.x, 0, floorBounds.max.z),
      ];

      createBoundaryFromPoints(boundaryPoints);

      // Create warning zone if requested
      if (options.showWarningZone) {
        const warningPoints = [
          new THREE.Vector3(
            floorBounds.min.x + warningZoneOffset,
            0,
            floorBounds.min.z + warningZoneOffset
          ),
          new THREE.Vector3(
            floorBounds.max.x - warningZoneOffset,
            0,
            floorBounds.min.z + warningZoneOffset
          ),
          new THREE.Vector3(
            floorBounds.max.x - warningZoneOffset,
            0,
            floorBounds.max.z - warningZoneOffset
          ),
          new THREE.Vector3(
            floorBounds.min.x + warningZoneOffset,
            0,
            floorBounds.max.z - warningZoneOffset
          ),
        ];

        createBoundaryFromPoints(warningPoints, true);
      }
    }

    return visualizations;
  }

  /**
   * Enable or disable rotation snapping
   * @param enabled Whether rotation snapping is enabled
   */
  setRotationSnappingEnabled(enabled: boolean): void {
    this._isRotationSnappingEnabled = enabled;
  }

  /**
   * Set the angle increment for rotation snapping
   * @param angle Angle in radians
   */
  setRotationSnapAngle(angle: number): void {
    this._rotationSnapAngle = angle;
  }

  /**
   * Snap a rotation angle to the nearest increment if rotation snapping is enabled
   * @param rotation Rotation value in radians
   * @param options Optional settings for rotation snapping
   * @returns The snapped rotation value
   */
  snapRotation(
    rotation: number,
    options: {
      snapAngle?: number; // Override the default snap angle
      snapStrength?: number; // How strong the snapping is (0-1)
    } = {}
  ): number {
    if (!this._isSnappingEnabled || !this._isRotationSnappingEnabled) {
      return rotation;
    }

    // Use provided snap angle or default
    const snapAngle = options.snapAngle ?? this._rotationSnapAngle;
    const snapStrength = options.snapStrength ?? 1.0;

    if (snapStrength <= 0 || snapAngle <= 0) {
      return rotation;
    }

    // Normalize rotation to 0-2π range
    const normalizedRotation =
      ((rotation % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);

    // Calculate snapped rotation
    const snappedRotation =
      Math.round(normalizedRotation / snapAngle) * snapAngle;

    // Apply snap strength (linear interpolation between original and snapped)
    let result =
      normalizedRotation +
      (snappedRotation - normalizedRotation) * snapStrength;

    // Keep in 0-2π range
    result = ((result % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);

    return result;
  }

  /**
   * Perform alignment snapping between objects
   * @param object Object to snap
   * @param scene Scene containing other objects
   * @param options Optional settings for alignment snapping
   * @returns Information about the snapping that occurred
   */
  snapAlignment(
    object: THREE.Mesh,
    scene: THREE.Scene,
    options: {
      threshold?: number; // Override the default snap threshold
      snapTypes?: string[]; // Types of alignment ('center', 'distribute', 'edge')
      visualize?: boolean; // Whether to show visual indicators
      snapStrength?: number; // How strong the snapping is (0-1)
      maxObjects?: number; // Maximum number of objects to consider
    } = {}
  ): {
    snapped: boolean;
    snapX: number;
    snapZ: number;
    snapType?: string;
    alignedObjects?: THREE.Mesh[];
  } {
    if (!this._isSnappingEnabled || !this._isEdgeSnappingEnabled) {
      return { snapped: false, snapX: 0, snapZ: 0 };
    }

    // Use provided options or defaults
    const threshold = options.threshold ?? this._snapThreshold * 2; // Wider threshold for alignment
    const visualize = options.visualize ?? true;
    const snapStrength = options.snapStrength ?? 1.0;
    const maxObjects = options.maxObjects ?? 5;
    const snapTypes = options.snapTypes ?? ['center', 'edge'];

    if (snapStrength <= 0) {
      return { snapped: false, snapX: 0, snapZ: 0 };
    }

    // Clear previous snap visualizations if visualizing
    if (visualize) {
      this.clearSnapVisualizations(scene);
    }

    // Find all relevant objects in the scene
    const otherObjects = scene.children.filter(
      (child) =>
        child instanceof THREE.Mesh &&
        child !== object &&
        child.userData.draggable &&
        !this.isLinkedToObject(object, child as THREE.Mesh) &&
        (!this._useCollisionGroups ||
          this.shouldObjectsCollide(object, child as THREE.Mesh))
    ) as THREE.Mesh[];

    if (otherObjects.length === 0) {
      return { snapped: false, snapX: 0, snapZ: 0 };
    }

    // Limit to closest objects for performance
    const objectBounds = getBounding(object);
    const objectCenter = new THREE.Vector3(
      (objectBounds.min.x + objectBounds.max.x) / 2,
      (objectBounds.min.y + objectBounds.max.y) / 2,
      (objectBounds.min.z + objectBounds.max.z) / 2
    );

    // Sort objects by distance to our object
    const sortedObjects = otherObjects
      .map((obj) => {
        const bounds = getBounding(obj);
        const center = new THREE.Vector3(
          (bounds.min.x + bounds.max.x) / 2,
          (bounds.min.y + bounds.max.y) / 2,
          (bounds.min.z + bounds.max.z) / 2
        );
        const distance = center.distanceTo(objectCenter);
        return { object: obj, distance };
      })
      .sort((a, b) => a.distance - b.distance)
      .slice(0, maxObjects) // Limit to maxObjects
      .map((item) => item.object);

    let snapX = 0;
    let snapZ = 0;
    let snapped = false;
    let snapType = '';
    const alignedObjects: THREE.Mesh[] = [];

    // Center alignment
    if (snapTypes.includes('center')) {
      // Check for vertical center alignment (same X)
      for (const otherObj of sortedObjects) {
        const otherBounds = getBounding(otherObj);
        const otherCenterX = (otherBounds.min.x + otherBounds.max.x) / 2;

        const diffX = Math.abs(objectCenter.x - otherCenterX);

        if (diffX < threshold) {
          const newSnapX = otherCenterX - objectCenter.x;
          snapX = newSnapX * snapStrength;
          snapped = true;
          snapType = 'centerX';
          alignedObjects.push(otherObj);

          // Visualize center alignment
          if (visualize) {
            this.createSnapVisualization(
              scene,
              new THREE.Vector3(otherCenterX, 0.01, objectBounds.min.z - 0.1),
              new THREE.Vector3(otherCenterX, 0.01, objectBounds.max.z + 0.1),
              0xff00ff
            );
          }
          break;
        }
      }

      // Check for horizontal center alignment (same Z)
      if (!snapped) {
        for (const otherObj of sortedObjects) {
          const otherBounds = getBounding(otherObj);
          const otherCenterZ = (otherBounds.min.z + otherBounds.max.z) / 2;

          const diffZ = Math.abs(objectCenter.z - otherCenterZ);

          if (diffZ < threshold) {
            const newSnapZ = otherCenterZ - objectCenter.z;
            snapZ = newSnapZ * snapStrength;
            snapped = true;
            snapType = 'centerZ';
            alignedObjects.push(otherObj);

            // Visualize center alignment
            if (visualize) {
              this.createSnapVisualization(
                scene,
                new THREE.Vector3(objectBounds.min.x - 0.1, 0.01, otherCenterZ),
                new THREE.Vector3(objectBounds.max.x + 0.1, 0.01, otherCenterZ),
                0xff00ff
              );
            }
            break;
          }
        }
      }
    }

    // Edge alignment
    if (!snapped && snapTypes.includes('edge')) {
      // Edges to check for alignment
      const objectLeft = objectBounds.min.x;
      const objectRight = objectBounds.max.x;
      const objectTop = objectBounds.max.z;
      const objectBottom = objectBounds.min.z;

      for (const otherObj of sortedObjects) {
        const otherBounds = getBounding(otherObj);
        const otherLeft = otherBounds.min.x;
        const otherRight = otherBounds.max.x;
        const otherTop = otherBounds.max.z;
        const otherBottom = otherBounds.min.z;

        // Check left edge alignment
        const diffLeft = Math.abs(objectLeft - otherLeft);
        if (diffLeft < threshold) {
          const newSnapX = otherLeft - objectLeft;
          snapX = newSnapX * snapStrength;
          snapped = true;
          snapType = 'leftEdge';
          alignedObjects.push(otherObj);

          if (visualize) {
            this.createSnapVisualization(
              scene,
              new THREE.Vector3(
                otherLeft,
                0.01,
                Math.min(objectBottom, otherBottom) - 0.1
              ),
              new THREE.Vector3(
                otherLeft,
                0.01,
                Math.max(objectTop, otherTop) + 0.1
              ),
              0x00ff00
            );
          }
          break;
        }

        // Check right edge alignment
        const diffRight = Math.abs(objectRight - otherRight);
        if (diffRight < threshold) {
          const newSnapX = otherRight - objectRight;
          snapX = newSnapX * snapStrength;
          snapped = true;
          snapType = 'rightEdge';
          alignedObjects.push(otherObj);

          if (visualize) {
            this.createSnapVisualization(
              scene,
              new THREE.Vector3(
                otherRight,
                0.01,
                Math.min(objectBottom, otherBottom) - 0.1
              ),
              new THREE.Vector3(
                otherRight,
                0.01,
                Math.max(objectTop, otherTop) + 0.1
              ),
              0x00ff00
            );
          }
          break;
        }

        // Check top edge alignment
        const diffTop = Math.abs(objectTop - otherTop);
        if (diffTop < threshold) {
          const newSnapZ = otherTop - objectTop;
          snapZ = newSnapZ * snapStrength;
          snapped = true;
          snapType = 'topEdge';
          alignedObjects.push(otherObj);

          if (visualize) {
            this.createSnapVisualization(
              scene,
              new THREE.Vector3(
                Math.min(objectLeft, otherLeft) - 0.1,
                0.01,
                otherTop
              ),
              new THREE.Vector3(
                Math.max(objectRight, otherRight) + 0.1,
                0.01,
                otherTop
              ),
              0x0000ff
            );
          }
          break;
        }

        // Check bottom edge alignment
        const diffBottom = Math.abs(objectBottom - otherBottom);
        if (diffBottom < threshold) {
          const newSnapZ = otherBottom - objectBottom;
          snapZ = newSnapZ * snapStrength;
          snapped = true;
          snapType = 'bottomEdge';
          alignedObjects.push(otherObj);

          if (visualize) {
            this.createSnapVisualization(
              scene,
              new THREE.Vector3(
                Math.min(objectLeft, otherLeft) - 0.1,
                0.01,
                otherBottom
              ),
              new THREE.Vector3(
                Math.max(objectRight, otherRight) + 0.1,
                0.01,
                otherBottom
              ),
              0x0000ff
            );
          }
          break;
        }
      }
    }

    // Distribution alignment - makes objects equally spaced
    if (
      !snapped &&
      snapTypes.includes('distribute') &&
      sortedObjects.length >= 2
    ) {
      // TODO: Implement distribution alignment in a future update
    }

    // Apply the snapping
    if (snapped) {
      object.position.x += snapX;
      object.position.z += snapZ;
    }

    return {
      snapped,
      snapX,
      snapZ,
      snapType,
      alignedObjects: alignedObjects.length > 0 ? alignedObjects : undefined,
    };
  }
}

// Create singleton instance
const snappingService = new SnappingService();
export { snappingService };
